/**
 * shared/assistant.ts —— AI 助手抽屉
 * 出处：01_需求文档/15_AI接入方案.md §四
 *
 * ## 它为什么不是 PageModule
 * 抽屉没有路由、没有骨架、不参与 `router/render.ts` 的竞态处理，
 * 所以 `frontend/README.md` 的 PageModule 契约对它**不适用**——不要为了"统一"把它塞进
 * `pages/registry.ts`，那会让 registry 里出现一个永远不会被 `pageOf()` 命中的条目。
 *
 * ## 入口只有一处
 * 运营总览 Hero 的那枚「✨ AI 助手」按钮（2026-08-24 管理员明确「其他地方暂不需要 AI 快速入口」）。
 * 🔴 `#/assistant` 那条路由与 `pages/assistant.ts` 已随本文件一起退役。
 *
 * ## DOM 挂在 body 上，不进 index.html
 * 抽屉要浮在两栏栅格之上，挂进 `#main` 会被主区的 `overflow` 裁掉。
 * 挂 body 且**懒创建**：没点过 AI 的人，页面上一个多余节点都没有。
 */
import type { AiConversationRef, AiFeedback, AiStreamEvent, AssistantBootstrap } from '@shared-types';
import { assistant } from '../api/index.js';
import { errText } from '../api/client.js';
import { $, esc } from './dom.js';
import { toast } from './common.js';
import { activeTeams } from './meta.js';
import { splitDraft } from './draft.js';

let root: HTMLElement | null = null;
let boot: AssistantBootstrap | null = null;
let convId = '';
/** 正在跑的那次问答。⚠ 关抽屉要 abort 它，否则回答会写进一个已经关掉的 DOM */
let inflight: AbortController | null = null;

/* ===================== 骨架 ===================== */

function ensureRoot(): HTMLElement {
  if (root) return root;
  const el = document.createElement('aside');
  el.className = 'aid';
  el.id = 'aid';
  el.setAttribute('aria-label', 'AI 助手');
  el.innerHTML = `
    <header class="aid-h">
      ${mark()}
      <span class="aid-title">训练营 AI 助手</span>
      <button class="aid-x" data-aid="hist" title="历史对话" aria-label="历史对话">${ICON_HIST}</button>
      <button class="aid-x" data-aid="new" title="新对话" aria-label="新对话">${ICON_NEW}</button>
      <button class="aid-x" data-aid="close" title="关闭" aria-label="关闭">${ICON_X}</button>
    </header>
    <div class="aid-body" id="aid-body"></div>
    <form class="aid-in" id="aid-form">
      <div class="aid-inbox">
        <textarea id="aid-q" rows="2" maxlength="500"
                  placeholder="请将训练营里的问题告诉我，使用 Shift+Enter 换行"></textarea>
        <div class="aid-in-r">
          <button class="aid-send" type="submit" aria-label="发送">${ICON_SEND}</button>
        </div>
      </div>
      <p class="aid-foot">内容由 AI 生成，仅供参考，请仔细核对</p>
    </form>`;
  /* 手机没有 Shift 键，提示里那半句只会让人困惑 */
  if (isPhone()) el.querySelector('#aid-q')?.setAttribute('placeholder', '请将训练营里的问题告诉我');
  document.body.appendChild(el);
  root = el;
  wire(el);
  return el;
}

/* 图标就地内联：只有本文件用，放进 shared/icons.ts 会让那份「侧栏 + 空态」的清单变杂 */
const ICON_X = '<svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"/></svg>';
const ICON_NEW = '<svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>';
const ICON_HIST = '<svg viewBox="0 0 24 24"><path d="M12 8v4l3 2"/><circle cx="12" cy="12" r="9"/></svg>';
/* ⚠ 发送键是**向上**的箭头（2026-08-25 用户指出原来那枚朝左）——这是聊天输入的通行手势 */
const ICON_SEND = '<svg viewBox="0 0 24 24"><path d="M12 19V5M6 11l6-6 6 6"/></svg>';
/* 引导卡的火花：AI 建议的标识。⚠ 描边式，与侧栏那套线性图标同一手感，不用实心 emoji */
const ICON_SPARK =
  '<svg viewBox="0 0 24 24"><path d="M12 4l1.7 4.8L18.5 10.5 13.7 12.2 12 17l-1.7-4.8L5.5 10.5 10.3 8.8z"/>' +
  '<path d="M18.6 15.4l.6 1.7 1.7.6-1.7.6-.6 1.7-.6-1.7-1.7-.6 1.7-.6z"/></svg>';

/**
 * 品牌标记：navy 方块 + 开口金环 + 暖橙 π，与侧栏 `.brand-mark` / favicon **逐参数同构**。
 * ⚠ **全面板只出现一次**（头部）。2026-08-25 问候区那枚已撤——同框两枚是重复。
 * ⚠ 颜色与两处动画（金环缓转 / π 呼吸）全在 `25_assistant.css`，这里只出结构——
 *   写死颜色会绕过换肤纪律（`frontend/README.md`）。
 */
function mark(): string {
  /* 🔴 2026-08-25：π 由「描边三笔」换成**填充型几何字形**，与 index.html 的侧栏标记 /
     public/favicon.svg 逐字节同一副 path（横梁切圆角 + 双腿外撇收分 + 9° 右倾）。
     换之前这里是全站第三套 π 字形——17px 下看着差不多，但和另两处并排就是两个 logo。
     ⚠ 环 r=13 / dasharray 61 21 / scale .60 三个参数也对齐了 favicon，别再各调各的。 */
  return `<span class="aid-mark" aria-hidden="true">
      <svg viewBox="0 0 32 32">
        <circle class="aid-ring" cx="16" cy="16" r="13" stroke-dasharray="61 21"/>
        <g class="aid-pi" transform="translate(16,16) scale(.60) translate(-16,-16)">
          <g transform="translate(2.6,0) skewX(-9)">
            <rect x="5" y="6.4" width="22" height="3.6" rx="1.8"/>
            <path d="M9.9 10 H13.5 L11.8 24.9 A1.75 1.75 0 0 1 8.3 24.55 Z"/>
            <path d="M19.2 10 H22.8 L24.9 24.55 A1.75 1.75 0 0 1 21.4 24.9 Z"/>
          </g>
        </g>
      </svg></span>`;
}

/* ===================== 打开 / 关闭 ===================== */

/** 手机档（与 `25_assistant.css` 满屏那条媒体查询同一个断点） */
const isPhone = (): boolean => window.matchMedia('(max-width:640px)').matches;

/**
 * 手机上把面板高度锁到**可视区**（2026-09-14）。
 * ⚠ iOS（含微信内置浏览器）弹键盘时不缩布局视口，`height:100%` 的 fixed 面板底部
 *   连同输入框会被键盘盖住；`visualViewport` 给的才是键盘之上真正看得见的那块。
 *   Android 会自己缩视口，这里算出来与 100% 相同，不冲突。
 */
function syncViewport(): void {
  const vv = window.visualViewport;
  if (!root || !vv) return;
  if (!isPhone()) {
    root.style.removeProperty('--aid-vh');
    root.style.removeProperty('--aid-vt');
    return;
  }
  root.style.setProperty('--aid-vh', `${vv.height}px`);
  root.style.setProperty('--aid-vt', `${vv.offsetTop}px`);
}
/* ⚠ typeof 守卫：单测在 node 下 import 本模块时没有 window */
if (typeof window !== 'undefined') {
  window.visualViewport?.addEventListener('resize', syncViewport);
  window.visualViewport?.addEventListener('scroll', syncViewport);
}

export function openAssistant(): void {
  const el = ensureRoot();
  el.classList.add('on');
  document.body.classList.add('aid-on');
  syncViewport();
  const q = $<HTMLTextAreaElement>('#aid-q');
  /* ⚠ 手机上不自动聚焦：一聚焦键盘就弹起来，把问候语和引导卡整块盖住，用户还没看到能问什么 */
  void refresh().then(() => {
    if (!isPhone()) q?.focus();
  });
}

export function closeAssistant(): void {
  /* ⚠ 必须 abort：流还在跑时关掉抽屉，回调会往已经不可见的 DOM 里写，
     再打开时会看到上一次问答的半截回答凭空续上 */
  inflight?.abort();
  inflight = null;
  root?.classList.remove('on');
  document.body.classList.remove('aid-on');
}

/** 拉引导数据并渲染空态。⚠ 每次打开都拉一次——配额是会变的 */
async function refresh(): Promise<void> {
  const body = $('#aid-body');
  if (!body) return;
  body.innerHTML = '<div class="aid-load">正在准备…</div>';
  try {
    boot = await assistant.bootstrap();
    convId = '';
    renderWelcome();
  } catch (e) {
    body.innerHTML = `<div class="aid-note">${esc(errText(e))}</div>`;
  }
}

function renderWelcome(): void {
  const body = $('#aid-body');
  if (!body || !boot) return;
  const disabled = !boot.enabled;
  /* ⚠ enabled=false 时按钮**照样在**，抽屉里说清原因（`15` §七）——
     隐藏入口只会让人以为坏了，这正是 2026-08-20 否掉「只预留布局位」的同一条理由 */
  /* ⚠ 问候区**不再放第二枚 π 徽标**（2026-08-25 用户指出重复）：
     头部那枚一直在，两枚同框只是把同一件事说两遍，还把问候语往下压了 40px。 */
  const hi = `<div class="aid-hi">
      <h2>你好，我是 <i>训练营 AI 助手</i></h2>
      <p>${disabled ? esc(boot.disabledReason) : '可以问队伍、成员、课题、OKR 与双周报。'}</p>
    </div>`;
  /* 🔴 **卡片标题也来自后端**（`promptGroups[].title`）：标题写死、指令后端发，
     管理员一改指令就会出现「标题写着双周报、卡里问的是别的」。红线 4 的同一条理由。 */
  const cards = disabled
    ? ''
    : `<div class="aid-cards">${boot.promptGroups
        .map(
          (g) => `<div class="aid-card">
             <div class="aid-card-h">${ICON_SPARK}<b>${esc(g.title)}</b></div>
             ${g.items
               .map((p) =>
                 /* ⚠ 点击行为也由服务端定（`mode`）：fill 型只填输入框、不发送，等用户接着写要点 */
                 p.mode === 'fill'
                   ? `<button class="aid-chip" data-aid="fill" data-fill="${esc(p.fill ?? '')}">${esc(p.text)}</button>`
                   : `<button class="aid-chip" data-aid="ask" data-q="${esc(p.text)}">${esc(p.text)}</button>`,
               )
               .join('')}
           </div>`,
        )
        .join('')}</div>`;
  body.innerHTML = `<div class="aid-welcome">${hi}${cards}${quotaHtml()}</div>`;
  setBusy(false);
}

/**
 * 🔴 正常态**不再显示「今日还可提问 N 次」**（2026-08-25 用户要求去掉）：
 *   空态是引导，不是仪表盘；把剩余次数摆在开场白下面，会让人先想"省着点用"。
 * ⚠ **预算耗尽这一档仍然要说** —— 那时候问了也不会有答案，不说等于让人对着输入框白试。
 */
function quotaHtml(): string {
  if (!boot || !boot.enabled) return '';
  return boot.quota.budgetExhausted
    ? '<p class="aid-quota">本月额度已用尽，历史对话仍可查看。</p>'
    : '';
}

/* ===================== 历史对话 ===================== */

async function showHistory(): Promise<void> {
  const body = $('#aid-body');
  if (!body || !boot) return;
  const list: AiConversationRef[] = boot.conversations;
  body.innerHTML = list.length
    ? `<div class="aid-hist"><div class="aid-hist-h">历史对话（仅你自己可见）</div>${list
        .map(
          (c) =>
            `<button class="aid-hist-i" data-aid="open" data-id="${esc(c.id)}">
               <span class="aid-hist-t">${esc(c.title)}</span>
               <span class="aid-hist-d">${esc(c.updatedAt.slice(0, 10))}</span>
             </button>`,
        )
        .join('')}</div>`
    : '<div class="aid-note">还没有历史对话。</div>';
}

async function openConversation(id: string): Promise<void> {
  const body = $('#aid-body');
  if (!body) return;
  body.innerHTML = '<div class="aid-load">正在读取…</div>';
  try {
    const d = await assistant.detail(id);
    convId = d.id;
    /* ⚠ 历史里的周报草稿同样切段、同样可复制——回头来抄草稿是最常见的回看理由 */
    body.innerHTML = d.messages
      .map((m) =>
        m.role === 'user'
          ? bubbleUser(m.content)
          : `<div class="aid-a"><div class="aid-txt">${answerBlockHtml(m.content)}</div>${toolsHtml(m.content, m.id, m.feedback)}</div>`,
      )
      .join('');
    body.scrollTop = body.scrollHeight;
  } catch (e) {
    body.innerHTML = `<div class="aid-note">${esc(errText(e))}</div>`;
  }
}

/* ===================== 回答渲染 ===================== */

const bubbleUser = (t: string): string => `<div class="aid-q">${esc(t)}</div>`;

/**
 * 把回答文本渲染成 HTML。
 *
 * 🔴 **先 esc 再做替换**，顺序不能反：反过来就是把模型输出当 HTML 注入。
 * ⚠ 队伍名变链接靠**后处理匹配真实队名表**，不靠模型输出 markdown 链接——
 *   模型会把 id 写错，而这里拿的是 `meta.activeTeams()` 里的真名，错不了（`15` §四之四）。
 * ⚠ 只匹配 2 个字以上的队名：一个字的队名会在正文里到处误命中。
 */
export function answerHtml(text: string): string {
  let h = esc(text);
  /* 极轻量的排版：**加粗** 与换行。⚠ 不引 markdown 库——
     回答里只有这两种结构，引一个库要多担 30KB 和一份 XSS 面 */
  h = h.replace(/\*\*([^*\n]{1,80})\*\*/g, '<b>$1</b>');

  const teams = activeTeams()
    .filter((t) => t.name.length >= 2)
    /* ⚠ 长名优先：不排序的话「一队」会把「一队攻坚组」切断 */
    .sort((a, b) => b.name.length - a.name.length);
  for (const t of teams) {
    const name = esc(t.name);
    /* ⚠ 已经在标签里的不再包一层：用 split/join 而不是全局正则回调，避免嵌套替换 */
    if (!h.includes(name)) continue;
    h = h
      .split(name)
      .join(`<a class="aid-tl" data-nav="#/overview/${esc(t.id)}">${name}</a>`);
  }
  return h.replace(/\n/g, '<br>');
}

/**
 * 回答块的正文。起草的周报（四个段落标题齐全）切成四段、每段带「复制本段」，
 * 对应周报编辑页的四个输入框；其余回答照旧（`answerHtml`）。
 * ⚠ 切段判定在 `draft.ts`，缺一个标题就不切——宁可不切，不可切错段。
 */
export function answerBlockHtml(text: string): string {
  const d = splitDraft(text);
  if (!d) return answerHtml(text);
  /* ⚠ 标签之间**不留空白**：`.aid-txt` 是 `pre-wrap`，模板里的换行缩进会渲染成空行 */
  return [
    d.head ? `<div class="aid-dh">${answerHtml(d.head)}</div>` : '',
    ...d.parts.map(
      (p) =>
        `<div class="aid-sec"><div class="aid-sec-h"><b>${esc(p.title)}</b>` +
        `<button class="aid-cp" data-aid="copy-sec">复制本段</button></div>` +
        `<div class="aid-sec-b">${answerHtml(p.body)}</div></div>`,
    ),
    d.tail ? `<div class="aid-dh">${esc(d.tail)}</div>` : '',
  ].join('');
}

/**
 * 回答下方的工具行。草稿叫「复制全部」（与每段的「复制本段」区分），普通回答叫「复制」。
 * 有 `messageId`（已落库的回答）才给「有用 / 没用」——没存进库的回答没有东西可评。
 * ⚠ 反馈只给本人看、只由本人改；再点一次已选中的那个 = 取消。
 */
export function toolsHtml(text: string, messageId?: string, fb: AiFeedback | null = null): string {
  const label = splitDraft(text) ? '复制全部' : '复制';
  const fbBtn = (v: AiFeedback, t: string): string =>
    `<button class="aid-cp aid-fb${fb === v ? ' on' : ''}" data-aid="fb" data-v="${v}" data-id="${esc(messageId ?? '')}" aria-pressed="${fb === v}">${t}</button>`;
  const fbs = messageId ? `${fbBtn('up', '有用')}${fbBtn('down', '没用')}` : '';
  return `<div class="aid-tools"><button class="aid-cp" data-aid="copy">${label}</button>${fbs}</div>`;
}

/** 提交反馈后按服务端返回的当前状态刷新同一行的两个按钮 */
async function sendFeedback(btn: HTMLElement): Promise<void> {
  const id = btn.dataset.id ?? '';
  const v = btn.dataset.v as AiFeedback;
  const row = btn.closest('.aid-tools');
  if (!id || !row || row.classList.contains('busy')) return;
  const next = btn.classList.contains('on') ? null : v;
  row.classList.add('busy');
  try {
    const r = await assistant.feedback(id, next);
    for (const b of row.querySelectorAll<HTMLElement>('.aid-fb')) {
      const on = b.dataset.v === r.feedback;
      b.classList.toggle('on', on);
      b.setAttribute('aria-pressed', String(on));
    }
    if (r.feedback) toast(r.feedback === 'up' ? '已标记为有用' : '已标记为没用，我们会据此改进');
  } catch (e) {
    toast(errText(e));
  } finally {
    row.classList.remove('busy');
  }
}

/**
 * 元素里的纯文本。⚠ 用 `innerText` 不用 `textContent`：换行在渲染时已变成 `<br>`，
 *   `textContent` 会把所有行粘成一行（2026-09-23 前的「复制」就是这样丢换行的）。
 */
const plain = (el: Element | null | undefined): string =>
  el instanceof HTMLElement ? el.innerText.trim() : (el?.textContent ?? '');

/**
 * 「复制全部」的文本。⚠ 切过段的回答不能直接取整块文本——
 *   会把每段的「复制本段」按钮文字一起复制走。按「标题 + 正文」重新拼。
 */
function copyText(box: Element | null | undefined): string {
  if (!box) return '';
  const secs = [...box.querySelectorAll('.aid-sec')];
  if (!secs.length) return plain(box);
  return secs
    .map((s) => `${plain(s.querySelector('.aid-sec-h b'))}\n${plain(s.querySelector('.aid-sec-b'))}`)
    .join('\n\n');
}

function copy(text: string): void {
  void navigator.clipboard
    .writeText(text)
    .then(() => toast('已复制'))
    .catch(() => toast('复制失败，请手动选中'));
}

/* ===================== 提问 ===================== */

function setBusy(on: boolean): void {
  root?.classList.toggle('busy', on);
  const btn = $<HTMLButtonElement>('.aid-send');
  if (btn) btn.disabled = on;
}

async function ask(question: string): Promise<void> {
  const body = $('#aid-body');
  if (!body || !boot?.enabled || root?.classList.contains('busy')) return;

  /* 第一次提问时把欢迎区换掉；后续追加 */
  if (body.querySelector('.aid-welcome') || body.querySelector('.aid-hist')) body.innerHTML = '';
  body.insertAdjacentHTML('beforeend', bubbleUser(question));
  /* 真实步骤条 + 打字光标都在这个壳里（`15` §四之三）*/
  body.insertAdjacentHTML(
    'beforeend',
    '<div class="aid-a live"><div class="aid-step"></div><div class="aid-txt"></div></div>',
  );
  const live = body.lastElementChild as HTMLElement;
  const step = live.querySelector<HTMLElement>('.aid-step');
  const txt = live.querySelector<HTMLElement>('.aid-txt');
  body.scrollTop = body.scrollHeight;
  setBusy(true);

  let acc = '';
  const ac = new AbortController();
  inflight = ac;
  try {
    await assistant.chat(
      { conversationId: convId || undefined, message: question },
      (e: AiStreamEvent) => {
        if (e.t === 'step' && step) {
          step.textContent = e.text;
          /* ⚠ 摘掉再挂回 class 之间必须读一次布局（`offsetWidth`），
             否则浏览器把两次 class 变更合并成"没变过"，动画不会重播 */
          step.classList.remove('go');
          void step.offsetWidth;
          step.classList.add('go');
          return;
        }
        if (e.t === 'delta' && txt) {
          acc += e.text;
          /* ⚠ 流式期间用 textContent（快、且天然安全），**收尾时才做一次链接化**——
             每个 delta 都重排 HTML 会让长回答肉眼可见地卡 */
          txt.textContent = acc;
          body.scrollTop = body.scrollHeight;
          return;
        }
        if (e.t === 'done') {
          convId = e.conversationId;
          step?.remove();
          if (txt) txt.innerHTML = answerBlockHtml(acc);
          live.classList.remove('live');
          live.insertAdjacentHTML('beforeend', toolsHtml(acc, e.messageId));
          return;
        }
        if (e.t === 'error') {
          step?.remove();
          live.classList.remove('live');
          live.classList.add('bad');
          if (txt) txt.textContent = acc ? `${acc}\n\n${e.message}` : e.message;
        }
      },
      ac.signal,
    );
  } catch (e) {
    if (!(e instanceof DOMException && e.name === 'AbortError')) {
      step?.remove();
      live.classList.remove('live');
      live.classList.add('bad');
      if (txt) txt.textContent = errText(e);
    }
  } finally {
    if (inflight === ac) inflight = null;
    setBusy(false);
    body.scrollTop = body.scrollHeight;
  }
}

/* ===================== 事件 ===================== */

function wire(el: HTMLElement): void {
  el.addEventListener('click', (ev) => {
    const t = ev.target;
    if (!(t instanceof Element)) return;
    const hit = t.closest<HTMLElement>('[data-aid]');
    if (!hit) return;
    switch (hit.dataset.aid) {
      case 'close':
        return closeAssistant();
      case 'new':
        convId = '';
        return renderWelcome();
      case 'hist':
        return void showHistory();
      case 'open':
        return void openConversation(hit.dataset.id ?? '');
      case 'ask':
        return void ask(hit.dataset.q ?? '');
      case 'fill': {
        const q = $<HTMLTextAreaElement>('#aid-q');
        /* 不再判 boot.enabled：停用时引导卡本来就不渲染，按钮不会存在 */
        if (!q) return;
        q.value = hit.dataset.fill ?? '';
        q.focus();
        q.setSelectionRange(q.value.length, q.value.length);
        /* 触发一次自动长高（与手动输入走同一条逻辑） */
        q.dispatchEvent(new Event('input'));
        return;
      }
      case 'copy':
        return copy(copyText(hit.closest('.aid-a')?.querySelector('.aid-txt')));
      case 'fb':
        return void sendFeedback(hit);
      case 'copy-sec':
        /* 只复制正文，不带标题——粘进的是已经写着标题的那个输入框 */
        return copy(plain(hit.closest('.aid-sec')?.querySelector('.aid-sec-b')));
    }
  });

  el.querySelector('#aid-form')?.addEventListener('submit', (ev) => {
    ev.preventDefault();
    const q = $<HTMLTextAreaElement>('#aid-q');
    const v = (q?.value ?? '').trim();
    if (!v) return;
    if (q) {
      q.value = '';
      q.style.height = '';
    }
    void ask(v);
  });

  /* Enter 发送 / Shift+Enter 换行。⚠ 输入法组合期间的 Enter 不能当发送
     （`isComposing`），否则中文选词会把半截拼音发出去 */
  el.querySelector('#aid-q')?.addEventListener('keydown', (ev) => {
    const e = ev as KeyboardEvent;
    if (e.key !== 'Enter' || e.shiftKey || e.isComposing) return;
    e.preventDefault();
    el.querySelector<HTMLFormElement>('#aid-form')?.requestSubmit();
  });

  /* 输入框跟着内容长高，封顶 4 行（与 `25_assistant.css` 的 max-height:96px 对齐——
     两处对不上时，JS 会把高度撑到 CSS 不允许的值，出现内滚动条） */
  el.querySelector('#aid-q')?.addEventListener('input', (ev) => {
    const ta = ev.target as HTMLTextAreaElement;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 96)}px`;
  });
}
